import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";

import { CopilotModalShell } from "../../CopilotModalShell";

const SOFT_BYTES_CAP = 250 * 1024 * 1024;
const MB = 1024 * 1024;
const KB = 1024;

function formatMMSS(ms) {
  const sec = Math.floor((ms || 0) / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatBytes(bytes) {
  if (!bytes || bytes < KB) return `${bytes || 0} B`;
  if (bytes < MB) return `${Math.round(bytes / KB)} KB`;
  // One decimal for MB so the cap-banner test can match either "249 MB" or "1.5 MB".
  return `${(bytes / MB).toFixed(1)} MB`;
}

const STATUS_LABEL = {
  recording: "Recording",
  captured: "Raw",
  compiling: "Compiling",
  compiled: "Compiled",
  compile_failed: "Compile failed",
};

function autoName(createdAt) {
  if (!createdAt) return "Recording";
  const d = new Date(createdAt);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  return `Recording ${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function displayName(draft) {
  return (draft.playbook && draft.playbook.name) || draft.name || autoName(draft.createdAt);
}

export const DraftsList = observer(function DraftsList({ controller }) {
  const drafts = controller.drafts || [];
  // { id, name } of the draft pending discard, or null. Drives the in-app
  // confirm so we don't fall back to a browser alert.
  const [pendingDiscard, setPendingDiscard] = useState(null);

  if (drafts.length === 0) return null;

  const totalBytes = controller.totalBytes || 0;
  const overSoftCap = totalBytes >= SOFT_BYTES_CAP;

  const confirmDiscard = () => {
    controller.discardDraft(pendingDiscard.id);
    setPendingDiscard(null);
  };

  return (
    <div className="cua-drafts">
      <div className="cua-drafts-header">
        Drafts ({drafts.length}) · {formatBytes(totalBytes)}
      </div>
      {overSoftCap && (
        <div className="cua-drafts-banner" role="status">
          Local storage is running low (near the {Math.round(SOFT_BYTES_CAP / MB)} MB cap). Discard older drafts before recording more.
        </div>
      )}
      <ul className="cua-drafts-list">
        {drafts.map((d) => (
          <DraftRow
            key={d.id}
            draft={d}
            controller={controller}
            onRequestDiscard={(name) => setPendingDiscard({ id: d.id, name })}
          />
        ))}
      </ul>

      {pendingDiscard && (
        <CopilotModalShell
          title="Discard draft?"
          size="sm"
          onClose={() => setPendingDiscard(null)}
          footer={
            <>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={() => setPendingDiscard(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--danger"
                onClick={confirmDiscard}
              >
                Discard
              </button>
            </>
          }
        >
          <p className="copilot-modal-body">
            Discard <strong>"{pendingDiscard.name}"</strong>? This can't be undone.
          </p>
        </CopilotModalShell>
      )}
    </div>
  );
});

const DraftRow = observer(function DraftRow({ draft, controller, onRequestDiscard }) {
  const [framesBytes, setFramesBytes] = useState(0);
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    controller.getFramesBytes(draft.id).then((n) => {
      if (!cancelled) setFramesBytes(n || 0);
    });
    controller.getFirstFrameBlob(draft.id).then((blob) => {
      if (cancelled || !blob) return;
      createdUrl = URL.createObjectURL(blob);
      setThumbUrl(createdUrl);
    });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [draft.id, controller]);

  const name = displayName(draft);
  const rowBytes = (draft.audioBytes || 0) + framesBytes;

  return (
    <li className="cua-drafts-row">
      <div className="cua-drafts-row-thumb">
        {thumbUrl ? <img src={thumbUrl} alt="" /> : <div className="cua-drafts-row-thumb-empty" />}
      </div>
      <div className="cua-drafts-row-main">
        <span className="cua-drafts-row-name">{name}</span>
        <span className="cua-drafts-row-meta">
          {formatMMSS(draft.durationMs)} · {formatBytes(rowBytes)} ·{" "}
          <span className={`cua-drafts-status cua-drafts-status-${draft.status || "captured"}`}>
            {STATUS_LABEL[draft.status] || STATUS_LABEL.captured}
          </span>
        </span>
      </div>
      <div className="cua-drafts-row-actions">
        <button
          type="button"
          className="cua-drafts-resume"
          onClick={() => controller.resumeDraft(draft.id)}
        >
          Resume
        </button>
        <button
          type="button"
          className="cua-drafts-discard"
          aria-label={`Discard ${name}`}
          onClick={() => onRequestDiscard(name)}
        >
          Discard
        </button>
      </div>
    </li>
  );
});
